import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

// Customer feedback ("Have your say"). Shows a top-bar button + a one-time
// login popup to account owners/admins/office once their org is 30+ days old.
export default function HaveYourSay() {
  const [prompt, setPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("missing_feature");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const autoOpened = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get("/feedback/prompt").then((r) => {
      if (!alive) return;
      setPrompt(r.data);
      if (r.data?.should_popup && !autoOpened.current) {
        autoOpened.current = true;
        setTimeout(() => alive && setOpen(true), 1200);
      }
    }).catch(() => alive && setPrompt({ eligible: false }));
    return () => { alive = false; };
  }, []);

  const dismiss = async () => {
    setOpen(false);
    setPrompt((p) => ({ ...p, should_popup: false }));
    try { await api.post("/feedback/dismiss"); } catch { /* best-effort */ }
  };

  const submit = async () => {
    if (!message.trim()) { toast.error("Tell us a little about it first"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { category, message: message.trim() });
      toast.success("Thanks — your feedback's gone straight to the Fieldlink team");
      setMessage("");
      setOpen(false);
      setPrompt((p) => ({ ...p, should_popup: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send your feedback");
    } finally { setBusy(false); }
  };

  if (!prompt?.eligible) return null;
  const cats = prompt.categories || [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="have-your-say-trigger"
        title="Have your say"
        aria-label="Have your say"
        className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#FF5F15]/40 bg-[#FF5F15]/5 text-[#FF5F15] hover:bg-[#FF5F15]/10 text-[12.5px] font-semibold transition-colors"
      >
        <MessageSquarePlus className="w-4 h-4" /> Have your say
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); else setOpen(true); }}>
        <DialogContent className="max-w-lg" data-testid="have-your-say-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#FF5F15]" /> Have your say
            </DialogTitle>
            <DialogDescription>
              We want to hear what you want as a customer. What is Fieldlink missing? What could be
              improved? What lets it down? Your feedback goes straight to our team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What's this about?</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="hys-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Your feedback</Label>
              <Textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1"
                placeholder="Tell us what you think…"
                data-testid="hys-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={dismiss} data-testid="hys-ignore">Not now</Button>
            <Button onClick={submit} disabled={busy} data-testid="hys-submit">
              {busy ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
